import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CircleOff, RefreshCw, ServerOff, WifiOff } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AreaChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { CopyableIp } from "@/components/copyable-ip";
import {
  AreaMetricsChart,
  formatTimestamp,
  getGpuColor,
  LineMetricsChart,
} from "@/components/metrics-chart";
import { OwnerAvatar } from "@/components/owner-avatar";
import { PoolPurposeBadge } from "@/components/pool-purpose-badge";
import { ProviderLogo } from "@/components/provider-logo";
import { type TimeRange, TimeRangeSelector } from "@/components/time-range-selector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useMachineMetrics } from "@/hooks/use-machine-metrics";
import { usePageTitle } from "@/hooks/use-page-title";
import { api } from "@/lib/api";
import { formatIdleDuration, idleReasonLabel } from "@/lib/format";
import { machineHost } from "@/lib/machine-host";
import type {
  AllocHistoryPoint,
  CpuMetricPoint,
  DcgmMetricPoint,
  GpuMetricPoint,
  IdleHistoryPoint,
  Machine,
  MetricsStatus,
} from "@/lib/types";
import { cn } from "@/lib/utils";

function StatusDot({ status }: { status: string }) {
  const dotColor =
    status === "running"
      ? "bg-emerald-500"
      : status === "starting" || status === "stopping"
        ? "bg-amber-500"
        : "bg-muted-foreground/40";
  const textColor =
    status === "running"
      ? "text-emerald-600 dark:text-emerald-400"
      : status === "starting" || status === "stopping"
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-2 w-2 rounded-full", dotColor)} />
      <span className={cn("text-[13px] capitalize", textColor)}>{status}</span>
    </span>
  );
}

/** Pivot GPU metrics: one row per timestamp with gpu_0, gpu_1, ... columns */
function pivotGpuMetrics(
  metrics: GpuMetricPoint[],
  field: keyof GpuMetricPoint,
): { data: Record<string, unknown>[]; gpuIndices: number[] } {
  const gpuIndices = [...new Set(metrics.map((point) => point.gpu_index))].sort(
    (indexA, indexB) => indexA - indexB,
  );
  const byTimestamp = new Map<string, Record<string, unknown>>();
  for (const point of metrics) {
    let row = byTimestamp.get(point.timestamp);
    if (!row) {
      row = { timestamp: point.timestamp };
      byTimestamp.set(point.timestamp, row);
    }
    row[`gpu_${point.gpu_index}`] = point[field];
  }
  return {
    data: [...byTimestamp.values()],
    gpuIndices,
  };
}

/** Pivot DCGM metrics similarly */
function pivotDcgmMetrics(
  metrics: DcgmMetricPoint[],
  field: keyof DcgmMetricPoint,
): { data: Record<string, unknown>[]; gpuIndices: number[] } {
  const gpuIndices = [...new Set(metrics.map((point) => point.gpu_index))].sort(
    (indexA, indexB) => indexA - indexB,
  );
  const byTimestamp = new Map<string, Record<string, unknown>>();
  for (const point of metrics) {
    let row = byTimestamp.get(point.timestamp);
    if (!row) {
      row = { timestamp: point.timestamp };
      byTimestamp.set(point.timestamp, row);
    }
    row[`gpu_${point.gpu_index}`] = point[field];
  }
  return {
    data: [...byTimestamp.values()],
    gpuIndices,
  };
}

const STATUS_CONFIG: Record<
  string,
  { icon: typeof AlertTriangle; title: string; className: string }
> = {
  no_clickhouse: {
    icon: ServerOff,
    title: "Metrics storage unavailable",
    className: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  },
  machine_stopped: {
    icon: CircleOff,
    title: "Machine is not running",
    className: "border-muted-foreground/20 bg-muted/50 text-muted-foreground",
  },
  no_agent: {
    icon: WifiOff,
    title: "No monitoring agent installed",
    className: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  },
  agent_stale: {
    icon: WifiOff,
    title: "Agent not reporting",
    className: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  },
  no_data: {
    icon: AlertTriangle,
    title: "No data for this range",
    className: "border-muted-foreground/20 bg-muted/50 text-muted-foreground",
  },
  query_failed: {
    icon: ServerOff,
    title: "Metrics query failed",
    className: "border-destructive/30 bg-destructive/5 text-destructive",
  },
  machine_not_found: {
    icon: AlertTriangle,
    title: "Machine not found",
    className: "border-destructive/30 bg-destructive/5 text-destructive",
  },
};

function MetricsStatusBanner({
  status,
  message,
  onRetry,
}: {
  status: MetricsStatus;
  message: string | null;
  onRetry?: () => void;
}) {
  if (status === "ok") return null;
  const fallback = {
    icon: AlertTriangle,
    title: "Unknown status",
    className: "border-destructive/30 bg-destructive/5 text-destructive",
  };
  const config = STATUS_CONFIG[status] ?? fallback;
  const Icon = config.icon;

  return (
    <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3", config.className)}>
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="flex flex-col gap-1">
        <span className="text-[13px] font-medium">{config.title}</span>
        {message && <span className="text-[12px] opacity-80">{message}</span>}
      </div>
      {(status === "query_failed" || status === "agent_stale") && onRetry && (
        <Button
          variant="outline"
          size="xs"
          onClick={onRetry}
          className="ml-auto shrink-0 gap-1 text-[12px]"
        >
          <RefreshCw className="size-3" />
          Retry
        </Button>
      )}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-4 w-32" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[250px] w-full" />
      </CardContent>
    </Card>
  );
}

function formatMemoryGib(valueMib: number): string {
  return `${(valueMib / 1024).toFixed(1)}`;
}

function formatNvlinkGbps(valueBytes: number): string {
  return (valueBytes / 1_000_000_000).toFixed(1);
}

interface IdleSpan {
  start: string;
  end: string;
  reason: string;
}

interface AllocMarker {
  timestamp: string;
  label: string;
  color: string;
}

function buildIdleSpans(idleHistory: IdleHistoryPoint[], machine: Machine): IdleSpan[] {
  const spans: IdleSpan[] = [];
  let currentStart: string | null = null;
  let currentReason = "";
  for (const point of idleHistory) {
    if (point.idle && !currentStart) {
      currentStart = point.timestamp;
      currentReason = point.reason || "idle";
    } else if (!point.idle && currentStart) {
      spans.push({ start: currentStart, end: point.timestamp, reason: currentReason });
      currentStart = null;
    }
  }
  if (currentStart) {
    spans.push({ start: currentStart, end: new Date().toISOString(), reason: currentReason });
  }
  if (machine.idle_since && spans.length === 0) {
    spans.push({
      start: machine.idle_since,
      end: new Date().toISOString(),
      reason: machine.idle_reason || "idle",
    });
  }
  return spans;
}

const rangeMs: Record<TimeRange, number> = {
  "1h": 3600000,
  "6h": 21600000,
  "24h": 86400000,
  "7d": 604800000,
  "30d": 2592000000,
};

// `/api/machines/{id}/metrics` returns the raw `AllocLog.action`, so the
// acquire value is "acquired" — never "acquire".
function allocMarkerLabel(event: AllocHistoryPoint): string {
  if (event.action === "acquired") return event.user_name || "?";
  if (event.action === "marked_disposable") return "Disposable";
  return "Released";
}

function buildAllocMarkers(allocHistory: AllocHistoryPoint[]): AllocMarker[] {
  return allocHistory.map((event) => ({
    timestamp: event.timestamp,
    label: allocMarkerLabel(event),
    color: event.action === "acquired" ? "oklch(0.55 0.15 145)" : "oklch(0.55 0.05 250)",
  }));
}

function ActivityTimeline({
  idleHistory,
  allocHistory,
  machine,
  range,
}: {
  idleHistory: IdleHistoryPoint[];
  allocHistory: AllocHistoryPoint[];
  machine: Machine;
  range: TimeRange;
}) {
  const idleSpans = useMemo(() => buildIdleSpans(idleHistory, machine), [idleHistory, machine]);

  const allocMarkers = useMemo(() => buildAllocMarkers(allocHistory), [allocHistory]);

  // Use numeric timestamps for reliable ReferenceArea positioning
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional recalc when range changes
  const nowMs = useMemo(() => Date.now(), [range]);
  const startMs = nowMs - (rangeMs[range] || 3600000);

  // Generate enough data points across the range so XAxis renders ticks
  const timelineData = useMemo(() => {
    const tickCount = 10;
    const step = (nowMs - startMs) / tickCount;
    const points = [];
    for (let i = 0; i <= tickCount; i++) {
      points.push({ ts: startMs + step * i, value: 0 });
    }
    return points;
  }, [startMs, nowMs]);

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] font-medium text-muted-foreground">
          Machine Activity (UTC)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={80}>
          <AreaChart data={timelineData} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[startMs, nowMs]}
              tickFormatter={(ms: number) => formatTimestamp(new Date(ms).toISOString(), range)}
              stroke="var(--color-muted-foreground)"
              tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
              tickLine={false}
              axisLine={false}
              minTickGap={40}
            />
            <YAxis hide domain={[0, 1]} allowDataOverflow />
            {idleSpans.map((span) => (
              <ReferenceArea
                key={`idle-${span.start}`}
                x1={new Date(span.start).getTime()}
                x2={new Date(span.end).getTime()}
                y1={0}
                y2={1}
                fill="#f59e0b"
                fillOpacity={0.3}
                strokeOpacity={0}
                label={{
                  value: `Idle — ${idleReasonLabel(span.reason)}`,
                  position: "insideLeft",
                  fontSize: 11,
                  fill: "#92400e",
                }}
              />
            ))}
            {allocMarkers.map((marker) => (
              <ReferenceLine
                key={`alloc-${marker.timestamp}`}
                x={new Date(marker.timestamp).getTime()}
                stroke={marker.color}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: marker.label,
                  position: "insideTopRight",
                  fontSize: 10,
                  fill: marker.color,
                }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function MachineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const machineId = Number(id);
  const [range, setRange] = useState<TimeRange>("1h");

  const {
    data: machine,
    isLoading: machineLoading,
    error: machineError,
  } = useQuery({
    queryKey: ["machine", machineId],
    queryFn: () => api.getMachine(machineId),
    enabled: machineId > 0,
  });
  usePageTitle(machine?.name ?? "Machine");

  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useMachineMetrics(machineId, range);

  const gpuUtilization = useMemo(
    () =>
      metrics?.gpu_metrics
        ? pivotGpuMetrics(metrics.gpu_metrics, "utilization_percent")
        : { data: [], gpuIndices: [] },
    [metrics?.gpu_metrics],
  );

  const gpuMemory = useMemo(
    () =>
      metrics?.gpu_metrics
        ? pivotGpuMetrics(metrics.gpu_metrics, "memory_used_mib")
        : { data: [], gpuIndices: [] },
    [metrics?.gpu_metrics],
  );

  const gpuTemperature = useMemo(
    () =>
      metrics?.gpu_metrics
        ? pivotGpuMetrics(metrics.gpu_metrics, "temperature_c")
        : { data: [], gpuIndices: [] },
    [metrics?.gpu_metrics],
  );

  const gpuPower = useMemo(
    () =>
      metrics?.gpu_metrics
        ? pivotGpuMetrics(metrics.gpu_metrics, "power_draw_w")
        : { data: [], gpuIndices: [] },
    [metrics?.gpu_metrics],
  );

  const cpuData = useMemo(
    () =>
      (metrics?.cpu_metrics ?? []).map((point: CpuMetricPoint) => ({
        timestamp: point.timestamp,
        cpu_util: point.cpu_util_percent,
        mem_used: point.mem_used_percent,
        load_1: point.load_avg_1,
        load_5: point.load_avg_5,
        load_15: point.load_avg_15,
      })),
    [metrics?.cpu_metrics],
  );

  const dcgmSmOccupancy = useMemo(
    () =>
      metrics?.dcgm_metrics?.length
        ? pivotDcgmMetrics(metrics.dcgm_metrics, "sm_occupancy")
        : { data: [], gpuIndices: [] },
    [metrics?.dcgm_metrics],
  );

  const dcgmTensorActive = useMemo(
    () =>
      metrics?.dcgm_metrics?.length
        ? pivotDcgmMetrics(metrics.dcgm_metrics, "tensor_active")
        : { data: [], gpuIndices: [] },
    [metrics?.dcgm_metrics],
  );

  const dcgmNvlinkTx = useMemo(
    () =>
      metrics?.dcgm_metrics?.length
        ? pivotDcgmMetrics(metrics.dcgm_metrics, "nvlink_tx_bytes")
        : { data: [], gpuIndices: [] },
    [metrics?.dcgm_metrics],
  );

  const processTimeline = useMemo(() => {
    const points = metrics?.process_timeline ?? [];
    if (!points.length) return { data: [], engines: [] };
    const engines = [...new Set(points.map((point) => point.engine || "unknown"))].sort();
    const byTimestamp = new Map<string, Record<string, unknown>>();
    for (const point of points) {
      let row = byTimestamp.get(point.timestamp);
      if (!row) {
        row = { timestamp: point.timestamp };
        for (const engine of engines) row[engine] = 0;
        byTimestamp.set(point.timestamp, row);
      }
      row[point.engine || "unknown"] = point.process_count;
    }
    return { data: [...byTimestamp.values()], engines };
  }, [metrics?.process_timeline]);

  const idle = machine ? formatIdleDuration(machine.idle_since) : null;

  if (machineLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="text-sm text-muted-foreground">Loading machine...</span>
      </div>
    );
  }

  if (machineError || !machine) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <span className="text-sm text-muted-foreground">
          {machineError ? `Error: ${(machineError as Error).message}` : "Machine not found"}
        </span>
        <Link to="/">
          <Button variant="outline" size="sm">
            Back to machines
          </Button>
        </Link>
      </div>
    );
  }

  const gpuSeries = (gpuIndices: number[]) =>
    gpuIndices.map((index) => ({
      dataKey: `gpu_${index}`,
      name: `GPU ${index}`,
      color: getGpuColor(index),
    }));

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            <span>Back to machines</span>
          </Link>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <h1 className="font-mono text-lg font-semibold text-foreground">{machine.name}</h1>
            <ProviderLogo provider={machine.provider} />
            {machine.provider === "nebius" && machine.provider_id && (
              <a
                href={`https://console.nebius.ai/compute/instances/${machine.provider_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                View in Nebius
              </a>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
            <StatusDot status={machine.cloud_status} />
            {machineHost(machine) && <CopyableIp ip={machineHost(machine) as string} />}
            {machine.owner_name && (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                Owner:
                <OwnerAvatar
                  name={machine.owner_name}
                  avatarUrl={machine.owner_avatar_url}
                  isService={machine.owner_is_service}
                  size="sm"
                />
                <span className="text-foreground">{machine.owner_name}</span>
              </span>
            )}
            <span className="font-mono text-muted-foreground">
              {machine.gpu_count}&times; {machine.gpu_type.toUpperCase()}
            </span>
            {machine.pool_purpose != null && <PoolPurposeBadge purpose={machine.pool_purpose} />}
            {machine.fabric && (
              <span className="rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {machine.fabric}
              </span>
            )}
            {machine.k8s_cluster_name && (
              <Link
                to="/k8s-clusters"
                className="inline-flex items-center gap-1 rounded-md border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[11px] font-medium text-violet-700 transition-colors hover:bg-violet-500/20 dark:text-violet-400"
              >
                K8s: {machine.k8s_cluster_name}
              </Link>
            )}
            {idle && (
              <span className="font-mono text-amber-600 dark:text-amber-400">
                Idle: {idle}
                {machine.idle_reason ? (
                  <span className="ml-1.5 text-[13px] font-sans font-normal text-amber-500/80">
                    ({idleReasonLabel(machine.idle_reason)})
                  </span>
                ) : null}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Nebius maintenance banner — restart required before the SLA deadline */}
      {machine?.maintenance_event_id && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-1">
            <span className="text-[13px] font-medium">Nebius maintenance restart required</span>
            <span className="text-[12px] opacity-80">
              This machine runs on hardware Nebius has flagged for maintenance. Stop and start it
              {machine.maintenance_deadline
                ? ` before ${new Date(machine.maintenance_deadline)
                    .toISOString()
                    .slice(0, 16)
                    .replace("T", " ")} UTC`
                : " soon"}{" "}
              or Nebius may interrupt it.
              {machine.maintenance_ticket_id
                ? ` (Nebius support ticket ${machine.maintenance_ticket_id})`
                : ""}
            </span>
          </div>
        </div>
      )}

      {/* Time range selector */}
      <div className="flex items-center justify-between">
        <TimeRangeSelector value={range} onChange={setRange} />
      </div>

      {/* Metrics status banner */}
      {metricsError && (
        <MetricsStatusBanner
          status="query_failed"
          message={(metricsError as Error).message}
          onRetry={() => refetchMetrics()}
        />
      )}
      {!metricsError && metrics && metrics.status !== "ok" && (
        <MetricsStatusBanner
          status={metrics.status}
          message={metrics.status_message}
          onRetry={
            metrics.status === "agent_stale" || metrics.status === "query_failed"
              ? () => refetchMetrics()
              : undefined
          }
        />
      )}

      {/* Machine Activity Timeline — always show */}
      {!metricsLoading && machine && (
        <ActivityTimeline
          idleHistory={metrics?.idle_history ?? []}
          allocHistory={metrics?.alloc_history ?? []}
          machine={machine}
          range={range}
        />
      )}

      {/* GPU Charts */}
      {metricsLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* GPU Utilization */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                GPU Utilization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AreaMetricsChart
                data={gpuUtilization.data}
                series={gpuSeries(gpuUtilization.gpuIndices)}
                range={range}
                unit="%"
                yDomain={[0, 100]}
              />
            </CardContent>
          </Card>

          {/* GPU Memory */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                GPU Memory (GiB)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AreaMetricsChart
                data={gpuMemory.data}
                series={gpuSeries(gpuMemory.gpuIndices)}
                range={range}
                unit=" GiB"
                valueFormatter={formatMemoryGib}
              />
            </CardContent>
          </Card>

          {/* GPU Temperature */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                GPU Temperature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LineMetricsChart
                data={gpuTemperature.data}
                series={gpuSeries(gpuTemperature.gpuIndices)}
                range={range}
                unit="°C"
              />
            </CardContent>
          </Card>

          {/* GPU Power Draw */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                GPU Power Draw
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LineMetricsChart
                data={gpuPower.data}
                series={gpuSeries(gpuPower.gpuIndices)}
                range={range}
                unit="W"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* CPU Charts */}
      {!metricsLoading && cpuData.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                CPU Utilization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AreaMetricsChart
                data={cpuData}
                series={[
                  {
                    dataKey: "cpu_util",
                    name: "CPU %",
                    color: "oklch(0.55 0.15 250)",
                  },
                ]}
                range={range}
                unit="%"
                yDomain={[0, 100]}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[13px] font-medium text-muted-foreground">
                System Memory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AreaMetricsChart
                data={cpuData}
                series={[
                  {
                    dataKey: "mem_used",
                    name: "Memory %",
                    color: "oklch(0.55 0.15 300)",
                  },
                ]}
                range={range}
                unit="%"
                yDomain={[0, 100]}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Process Timeline */}
      {!metricsLoading && processTimeline.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[13px] font-medium text-muted-foreground">
              Inference Engine Processes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AreaMetricsChart
              data={processTimeline.data}
              series={processTimeline.engines.map((engine, index) => ({
                dataKey: engine,
                name: engine || "unknown",
                color: getGpuColor(index),
              }))}
              range={range}
              unit=""
              yDomain={[0, "auto" as unknown as number]}
              valueFormatter={(value: number) => Math.round(value).toString()}
              allowDecimals={false}
            />
          </CardContent>
        </Card>
      )}

      {/* DCGM Charts */}
      {!metricsLoading && metrics?.dcgm_metrics && metrics.dcgm_metrics.length > 0 && (
        <>
          <h2 className="text-[13px] font-medium uppercase tracking-wider text-muted-foreground">
            DCGM Metrics
          </h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-[13px] font-medium text-muted-foreground">
                  SM Occupancy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AreaMetricsChart
                  data={dcgmSmOccupancy.data}
                  series={gpuSeries(dcgmSmOccupancy.gpuIndices)}
                  range={range}
                  unit="%"
                  yDomain={[0, 100]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-[13px] font-medium text-muted-foreground">
                  Tensor Active
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AreaMetricsChart
                  data={dcgmTensorActive.data}
                  series={gpuSeries(dcgmTensorActive.gpuIndices)}
                  range={range}
                  unit="%"
                  yDomain={[0, 100]}
                />
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-[13px] font-medium text-muted-foreground">
                  NVLink TX Throughput (GB/s)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LineMetricsChart
                  data={dcgmNvlinkTx.data}
                  series={gpuSeries(dcgmNvlinkTx.gpuIndices)}
                  range={range}
                  unit=" GB/s"
                  valueFormatter={formatNvlinkGbps}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
